import { addDays } from "date-fns";
import { ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import {
  BookingSchedule,
  BookingScheduleValidationOptions,
} from "@/lib/types/schedule-types";
import { cn } from "@/lib/utils";
import {
  dateToBookingScheduleDateTime,
  defaultScheduleDay,
  validateScheduleDay,
} from "@/lib/utils/schedule-utils";
import { ScheduleDayInputs, ScheduleDayInputsProps } from "./ScheduleDayInputs";

export type ScheduleInputFieldProps = Omit<ScheduleDayInputsProps, "name"> & {
  fullWidth?: boolean;
  testId?: string;
  name?: string;
  extendDayDisabled?: boolean;
  className?: string;
  dayPickerDataTestingId?: string;
  hideExtendDayBtn?: boolean;
  note?: ReactNode;
};

export const ScheduleInputField = ({
  name = "schedule",
  extendDayDisabled = false,
  fullWidth = true,
  datePickerProps,
  timeSelectProps,
  getCalendarModifiers,
  getTimeOptions,
  testId = "schedule-input",
  locale = "en-US",
  className,
  dayPickerDataTestingId,
  hideExtendDayBtn,
  note,
  ...containerProps
}: ScheduleInputFieldProps) => {
  const { watch, setValue } = useFormContext();

  const scheduleValue = watch(name) as BookingSchedule;
  const currentSchedule = scheduleValue?.length
    ? scheduleValue
    : [defaultScheduleDay];

  const lastDay = currentSchedule[currentSchedule.length - 1];

  const addDay = () => {
    const lastStartDate = new Date(
      lastDay.start.date?.y as number,
      (lastDay.start.date?.m as number) - 1,
      lastDay.start.date?.d,
    );

    const lastEndDate = new Date(
      lastDay.end.date?.y as number,
      (lastDay.end.date?.m as number) - 1,
      lastDay.end.date?.d,
    );

    setValue(name, [
      ...currentSchedule,
      {
        start: {
          ...lastDay.start,
          date: dateToBookingScheduleDateTime(addDays(lastStartDate, 1))?.date,
        },
        end: {
          ...lastDay.end,
          date: dateToBookingScheduleDateTime(addDays(lastEndDate, 1))?.date,
        },
      },
    ]);
  };

  const removeDay = () => {
    setValue(name, currentSchedule.slice(0, currentSchedule.length - 1));
  };

  return (
    <div
      className={cn(
        "tw-flex tw-flex-col tw-gap-16",
        {
          "tw-w-full": fullWidth,
        },
        className,
      )}
      {...containerProps}
    >
      {currentSchedule.map((_, i: number) => (
        <ScheduleDayInputs
          key={`${name}-${i}`}
          name={`${name}.${i}`}
          locale={locale}
          getCalendarModifiers={getCalendarModifiers}
          getTimeOptions={getTimeOptions}
          testId={`${testId}-day-${i}`}
          datePickerProps={datePickerProps}
          timeSelectProps={timeSelectProps}
          dayPickerDataTestingId={`${dayPickerDataTestingId}-${i}`}
        />
      ))}

      {(!hideExtendDayBtn || !!note) && (
        <div className="tw-flex tw-justify-between tw-items-center">
          {!hideExtendDayBtn && (
            <Button
              testId="extend-day-button"
              className="tw-body-1-underline"
              variant="underline"
              appearance="purple"
              onClick={addDay}
              disabled={
                extendDayDisabled ||
                !validateScheduleDay(lastDay, {
                  exactDuration:
                    timeSelectProps?.autoCorrectConfig?.exactDuration,
                  minDuration: timeSelectProps?.autoCorrectConfig?.minDuration,
                } as BookingScheduleValidationOptions).scheduleDay
              }
            >
              <FormattedMessage defaultMessage="Extend a day" />
            </Button>
          )}

          <div className="tw-flex tw-items-center tw-gap-8">
            {!!note && <Text typeface="body-1">{note}</Text>}

            {currentSchedule.length > 1 && !hideExtendDayBtn && (
              <Button
                className="tw-body-1-underline"
                variant="underline"
                onClick={removeDay}
              >
                <FormattedMessage defaultMessage="Remove" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
